import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../component-styles/ReportsShared.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';


function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

function getDefaultDateRange() {
  const year = new Date().getFullYear();
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

function normalizeProject(raw) {
  return {
    id: String(raw.projectId || raw.id || raw.name || ''),
    name: raw.projectNickname || raw.name || raw.title || 'Untitled Project',
  };
}

export default function RoomByRoomReportPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [projectFilter, setProjectFilter] = useState('all');
  const [roomFilter, setRoomFilter] = useState('all');
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);

  useEffect(() => {
    async function loadProjects() {
      if (!userId) {
        setProjects([]);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data?.Items || data?.items || [];
        setProjects(arr.map(normalizeProject));
      } catch (err) {
        console.error('Failed to load projects', err);
        setProjects([]);
      }
    }

    loadProjects();
  }, [userId]);

  useEffect(() => {
    async function loadLibrary() {
      if (!userId) {
        setItems([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/library/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data?.items) ? data.items : [];
        setItems(list);
      } catch (err) {
        console.error('Failed to load library items', err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    }

    loadLibrary();
  }, [userId]);

  const rooms = useMemo(() => {
    const set = new Set();
    items.forEach((item) => {
      if (projectFilter !== 'all' && String(item.projectId || '') !== projectFilter) return;
      const room = item.room || 'Unassigned';
      set.add(room);
    });
    return Array.from(set).sort();
  }, [items, projectFilter]);

  useEffect(() => {
    if (roomFilter !== 'all' && !rooms.includes(roomFilter)) {
      setRoomFilter('all');
    }
  }, [roomFilter, rooms]);

  const filteredRows = useMemo(() => {
    return items.filter((item) => {
      if (projectFilter !== 'all' && String(item.projectId || '') !== projectFilter) return false;
      if (roomFilter !== 'all' && (item.room || 'Unassigned') !== roomFilter) return false;
      const date = String(item.createdAt || item.updatedAt || '').slice(0, 10);
      if (startDate && date && date < startDate) return false;
      if (endDate && date && date > endDate) return false;
      return true;
    });
  }, [items, projectFilter, roomFilter, startDate, endDate]);

  const projectMap = useMemo(() => {
    return projects.reduce((acc, p) => {
      acc[String(p.id)] = p.name;
      return acc;
    }, {});
  }, [projects]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, item) => {
        acc.qty += Number(item.qty || 0);
        acc.total += Number(item.lineTotal || item.lineMerch || 0);
        return acc;
      },
      { qty: 0, total: 0 }
    );
  }, [filteredRows]);

  function exportExcel() {
    const headers = ['Project', 'Room', 'Item', 'Qty', 'Trade Price', 'Line Total', 'Date Added'];
    const rowsForExport = filteredRows.map((item) => [
      projectMap[String(item.projectId || '')] || 'Untitled Project',
      item.room || 'Unassigned',
      item.name || '',
      Number(item.qty || 0),
      Number(item.tradePrice || 0),
      Number(item.lineTotal || item.lineMerch || 0),
      item.createdAt || item.updatedAt ? String(item.createdAt || item.updatedAt).slice(0, 10) : '',
    ]);

    exportRowsToExcel({
      filename: 'room-by-room-report.xlsx',
      sheetName: 'Room by Room',
      headers,
      rows: rowsForExport,
    });
  }

  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="report-header">
          <div className="report-title">
            <h1>Room by Room Item List</h1>
            <div className="muted">
              {startDate} - {endDate}
            </div>
          </div>
          <div className="report-actions">
            <button className="report-btn-secondary" onClick={() => navigate('/reports')}>
              Back
            </button>
            <button className="report-btn-primary" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>

        <div className="report-filters">
          <select
            className="report-select"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="all">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <select
            className="report-select"
            value={roomFilter}
            onChange={(e) => setRoomFilter(e.target.value)}
          >
            <option value="all">All Rooms</option>
            {rooms.map((room) => (
              <option key={room} value={room}>
                {room}
              </option>
            ))}
          </select>

          <input
            className="report-input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            className="report-input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="report-card">
          <div className="report-metrics">
            <div className="report-metric">
              <div className="report-metric-value">{totals.qty}</div>
              <div className="report-metric-label">Total Items</div>
            </div>
            <div className="report-metric">
              <div className="report-metric-value">{formatCurrency(totals.total)}</div>
              <div className="report-metric-label">Total Value</div>
            </div>
          </div>
        </div>

        <div className="report-table-wrap">
          {loading ? (
            <div className="report-empty">Loading library items...</div>
          ) : filteredRows.length === 0 ? (
            <div className="report-empty">No items found.</div>
          ) : (
            <table className="report-table" role="table" aria-label="Room by room item list">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Room</th>
                  <th>Item</th>
                  <th className="right">Qty</th>
                  <th className="right">Trade Price</th>
                  <th className="right">Line Total</th>
                  <th>Date Added</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((item) => (
                  <tr key={item.sk || item.id || item.name}>
                    <td>{projectMap[String(item.projectId || '')] || 'Untitled Project'}</td>
                    <td>{item.room || 'Unassigned'}</td>
                    <td>{item.name}</td>
                    <td className="right">{Number(item.qty || 0)}</td>
                    <td className="right">{formatCurrency(item.tradePrice || 0)}</td>
                    <td className="right">
                      {formatCurrency(item.lineTotal || item.lineMerch || 0)}
                    </td>
                    <td>{formatDate(item.createdAt || item.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
